import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Label } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const MyDetailsCard = () => {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/me/profile").then((r) => setP(r.data)).catch(() => {}); }, []);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 1024 * 1024) { toast.error("Photo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setP((x) => ({ ...x, photo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!p.name || !p.name.trim()) { toast.error("Name cannot be empty."); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/me/profile", { name: p.name.trim(), photo_url: p.photo_url, phone: (p.phone || "").trim() });
      setP(data);
      // Broadcast so the portal header (and anything else keyed off the auth user) refreshes.
      window.dispatchEvent(new CustomEvent("sv:profile-updated", { detail: data }));
      toast.success("Profile updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!p) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="my-details-card">
      <Label>My Details</Label>
      <p className="mt-1 text-sm text-slate-500">Your photo and display name appear on your Service CV. Job title and location are managed by your manager.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 flex items-center gap-4">
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50">
            {p.photo_url ? <img src={p.photo_url} alt="" className="h-full w-full object-cover" data-testid="my-photo-preview" /> : <span className="text-[10px] text-slate-400">No photo</span>}
          </div>
          <div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="my-photo-label">
              Upload photo<input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="my-photo-input" />
            </label>
            {p.photo_url && <button type="button" onClick={() => setP({ ...p, photo_url: "" })} className="ml-2 text-sm font-semibold text-red-600 hover:underline" data-testid="my-photo-remove">Remove</button>}
          </div>
        </div>
        <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">Display name</Label>
          <Input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="my-name" /></div>
        <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">Phone number</Label>
          <Input value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="+27…" data-testid="my-phone" /></div>
        <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">Job title</Label>
          <Input value={p.job_title || "—"} disabled data-testid="my-jobtitle" /></div>
        <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">Location</Label>
          <Input value={p.location_name || "—"} disabled data-testid="my-location" /></div>
      </div>
      <Button onClick={save} disabled={saving || !p.name} data-testid="save-my-details" className="mt-4 bg-[#4338CA] hover:bg-[#3730A3]">Save details</Button>
    </div>
  );
};
